import { compile } from 'path-to-regexp';

import Console from 'utility/Debug/Console';
import { dispatchStatsDEventWithTags } from 'utility/Track/statsD';

import {
    CallOptions,
    ContentType,
    HttpResponse,
    RequestBuilder,
    ResponseParser,
} from '../types';

export function buildRequestUrl<UrlParams>(
    url: URL | string,
    queryParams?: URLSearchParams,
    urlParams?: UrlParams
): string {
    if (urlParams) {
        // replace url param placeholder with concrete values
        url = compile<Partial<UrlParams>>(url.toString())(urlParams);
    }
    if (!queryParams) {
        // no query params given -> just return the url
        return url.toString();
    }
    const parsedUrl = url instanceof URL ? url : new URL(url);
    let urlSearchParams: URLSearchParams;
    if (parsedUrl.search) {
        // given url already contains query params -> append the given ones
        urlSearchParams = parsedUrl.searchParams;
        queryParams.forEach((value, key) => {
            urlSearchParams.append(key, value);
        });
    } else {
        // given url does not contain query params -> just set the given ones
        urlSearchParams = queryParams;
    }
    const encodedParams = urlSearchParams.toString();
    parsedUrl.search = encodedParams ? `?${encodedParams}` : '';

    return parsedUrl.toString();
}

/**
 * basic fetch wrapper, used to handle request building and response parsing
 *
 * @param options
 * @param requestBuilder
 * @param responseParser
 */
export async function http<RequestType, ResponseType, UrlParams>(
    options: CallOptions<RequestType, UrlParams>,
    requestBuilder: RequestBuilder<RequestType, UrlParams>,
    responseParser: ResponseParser<ResponseType>
): Promise<HttpResponse<ResponseType>> {
    const request = requestBuilder(options);
    const response = await fetch(request);
    let parsedBody: ResponseType | undefined;
    if (response.ok) {
        parsedBody = await responseParser(response);
        Console.info('response parsed:', parsedBody);
    } else if (request.headers.get('Content-Type') === ContentType.Protobuf) {
        dispatchStatsDEventWithTags('protobuf.communication.error', {
            url: request.url,
            statusCode: String(response.status),
            statusText: response.statusText,
        });
    }
    // enrich native response with parsedBody and signal, if present
    return {
        ...response,
        parsedBody,
        signal: options?.requestOptions?.signal ?? undefined,
    };
}
